// src/service/SavingsService.js
//
// Savings accounts: one record per account, its past values kept on the same
// record. Both partners read and write the same rows, matched on the family
// group the way shared expenses are.

import uuid from "react-uuid";
import { API_BASE, API_HEADERS, getData } from "./APIService";

const AI_LIST = (value) =>
  Array.from(
    new Set(
      String(value || "")
        .split(",")
        .map((entry) => entry.trim().toLowerCase())
        .filter(Boolean)
    )
  );

const SAVINGS_AUDIENCE = String(process.env.REACT_APP_SAVINGS_USER || "").trim();
const SAVINGS_FOR_EVERYONE = SAVINGS_AUDIENCE.toUpperCase() === "ALL";
const SAVINGS_ALLOWED = new Set(AI_LIST(SAVINGS_AUDIENCE));

/**
 * Whether this person gets the savings page at all. Empty list means nobody:
 * unlike the voice feature there is no separate on switch, so the list is the
 * switch.
 */
export const isSavingsEnabled = (email) => {
  if (SAVINGS_FOR_EVERYONE) return true;
  if (SAVINGS_ALLOWED.size === 0) return false;

  let who = String(email || "").trim().toLowerCase();
  if (!who) {
    try {
      who = String(localStorage.getItem("loggedInUserEmail") || "").trim().toLowerCase();
    } catch {
      return false;
    }
  }
  return !!who && SAVINGS_ALLOWED.has(who);
};

/** The family group, as a stable key whatever order the members were stored in. */
export const groupKeyOf = (value) => AI_LIST(value).sort().join(",");

export const BUCKETS = [
  "In hand",
  "1-year",
  "3-year",
  "5-year",
  "10-year",
  "15-year",
  "60-year lock-in",
];

export const ALL_CURRENCIES = [
  { code: "INR", symbol: "₹", name: "Indian Rupee" },
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "CAD", symbol: "CA$", name: "Canadian Dollar" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar" },
  { code: "AED", symbol: "AED ", name: "UAE Dirham" },
  { code: "JPY", symbol: "¥", name: "Japanese Yen" },
  { code: "BRL", symbol: "R$", name: "Brazilian Real" },
  { code: "MXN", symbol: "MX$", name: "Mexican Peso" },
  { code: "UYU", symbol: "$U", name: "Uruguayan Peso" },
];

export const CURRENCIES = ALL_CURRENCIES.slice(0, 2);

const CURRENCY_CONFIG_KEY = "savings_currencies_config";

export const getSavingsCurrencyConfig = () => {
  const fallback = {
    enabledCodes: ["INR", "USD"],
    defaultCode: "INR",
    customCurrencies: [],
  };
  try {
    const raw = localStorage.getItem(CURRENCY_CONFIG_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    const customCurrencies = Array.isArray(parsed?.customCurrencies)
      ? parsed.customCurrencies
          .filter((c) => c && c.code && c.symbol)
          .map((c) => ({
            code: String(c.code).trim().toUpperCase(),
            symbol: String(c.symbol).trim(),
            name: String(c.name || c.code).trim(),
          }))
      : [];
    const enabledCodes = Array.isArray(parsed?.enabledCodes)
      ? Array.from(
          new Set(
            parsed.enabledCodes
              .map((c) => String(c || "").trim().toUpperCase())
              .filter(Boolean)
          )
        )
      : ["INR", "USD"];
    const finalCodes = enabledCodes.length > 0 ? enabledCodes : ["INR", "USD"];
    const reqDefault = String(parsed?.defaultCode || "").trim().toUpperCase();
    const defaultCode = finalCodes.includes(reqDefault) ? reqDefault : finalCodes[0];
    return { enabledCodes: finalCodes, defaultCode, customCurrencies };
  } catch {
    return fallback;
  }
};

export const getAllAvailableCurrencies = () => {
  const { customCurrencies } = getSavingsCurrencyConfig();
  const map = new Map();
  ALL_CURRENCIES.forEach((c) => map.set(c.code, c));
  customCurrencies.forEach((c) => {
    if (!map.has(c.code)) map.set(c.code, c);
    else map.set(c.code, { ...map.get(c.code), ...c });
  });
  return Array.from(map.values());
};

export const getConfiguredCurrencies = () => {
  const { enabledCodes } = getSavingsCurrencyConfig();
  const all = getAllAvailableCurrencies();
  const map = new Map(all.map((c) => [c.code, c]));
  const result = enabledCodes
    .map((code) => map.get(code) || { code, symbol: code, name: code })
    .filter(Boolean);
  return result.length > 0 ? result : CURRENCIES;
};

export const saveSavingsCurrencyConfig = (next) => {
  const current = getSavingsCurrencyConfig();
  const customCurrencies = Array.isArray(next?.customCurrencies)
    ? next.customCurrencies
        .filter((c) => c && c.code && c.symbol)
        .map((c) => ({
          code: String(c.code).trim().toUpperCase(),
          symbol: String(c.symbol).trim(),
          name: String(c.name || c.code).trim(),
        }))
    : current.customCurrencies;

  let enabledCodes = Array.isArray(next?.enabledCodes)
    ? Array.from(
        new Set(
          next.enabledCodes
            .map((c) => String(c || "").trim().toUpperCase())
            .filter(Boolean)
        )
      )
    : current.enabledCodes;

  if (enabledCodes.length === 0) {
    enabledCodes = ["INR"];
  }

  const reqDefault = String(next?.defaultCode || current.defaultCode || "").trim().toUpperCase();
  const defaultCode = enabledCodes.includes(reqDefault) ? reqDefault : enabledCodes[0];

  const normalized = { enabledCodes, defaultCode, customCurrencies };
  try {
    localStorage.setItem(CURRENCY_CONFIG_KEY, JSON.stringify(normalized));
    window.dispatchEvent(new CustomEvent("savings-currencies-updated", { detail: normalized }));
  } catch {
    /* ignore storage errors */
  }
  return normalized;
};

/**
 * Every account belonging to this group, however it was stored.
 * Rows are fetched per member because the table is queried by email.
 */
export const getSavings = async (members) => {
  const emails = AI_LIST(members);
  if (emails.length === 0) return [];
  const key = emails.slice().sort().join(",");

  try {
    const perMember = await Promise.all(emails.map((email) => getData(email, "saving").catch(() => [])));
    const seen = new Set();
    return perMember.flat().filter((row) => {
      if (!row || seen.has(row.id)) return false;
      // a row saved before the family was linked carries only its writer's email
      if (groupKeyOf(row.group || row.email) !== key) return false;
      seen.add(row.id);
      return true;
    });
  } catch (err) {
    console.log("Error getting savings:", err);
    return [];
  }
};

const post = async (row) => {
  const res = await fetch(`${API_BASE}/items`, {
    method: "POST",
    headers: API_HEADERS,
    body: JSON.stringify(row),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return row;
};

/**
 * A new account. `email` is the row's owner for the query above; `owner` is the
 * person it belongs to, which is a different thing — Subha can add an account
 * that is Bala's.
 */
export const createSaving = async ({ name, owner, bucket, currency, value, note, countInTotal = true, group, email, by }) => {
  const now = new Date().toISOString();
  const amount = Number(value) || 0;

  return post({
    id: uuid(),
    type: "saving",
    name: String(name || "").trim(),
    owner,
    bucket,
    currency,
    value: amount,
    note: String(note || "").trim(),
    countInTotal,
    group: groupKeyOf(group || email),
    email,
    updatedBy: by,
    updatedAt: now,
    history: [{ value: amount, at: now, by }],
  });
};

// Longer than either of them will ever scroll, and still a few KB
const MAX_HISTORY = 120;

/** A new balance, keeping what it was before it (or updating edited history). */
export const updateSaving = async (
  row,
  { value, note, countInTotal, bucket, owner, currency, history: nextHistory, replaceHistory = false, by }
) => {
  const now = new Date().toISOString();
  const baseHistory = Array.isArray(nextHistory)
    ? nextHistory
        .map((item) => ({
          value: Number(item.value) || 0,
          at: item.at || now,
          by: item.by || by || row.updatedBy || row.email,
        }))
        .sort((a, b) => String(b.at).localeCompare(String(a.at)))
    : Array.isArray(row.history)
    ? row.history.slice()
    : [];

  const latestInHistory = baseHistory.length > 0 ? Number(baseHistory[0].value) || 0 : Number(row.value) || 0;
  const amount =
    replaceHistory && value === undefined
      ? latestInHistory
      : value === undefined
      ? Number(row.value) || 0
      : Number(value) || 0;

  const history = baseHistory.slice();
  if (!replaceHistory && amount !== latestInHistory) {
    history.unshift({ value: amount, at: now, by });
  }

  return post({
    ...row,
    value: amount,
    currency: currency || row.currency || "INR",
    note: note === undefined ? row.note : String(note || "").trim(),
    countInTotal: countInTotal === undefined ? row.countInTotal !== false : countInTotal,
    bucket: bucket || row.bucket,
    owner: owner || row.owner,
    updatedBy: by || row.updatedBy,
    updatedAt: replaceHistory && history[0]?.at ? history[0].at : now,
    history: history.slice(0, MAX_HISTORY),
  });
};

export const deleteSaving = async (id) => {
  const res = await fetch(`${API_BASE}/removeitem/${id}`, { method: "DELETE", headers: API_HEADERS });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
};

/* ----------------------------- reading the numbers ----------------------------- */

export const counts = (row) => row && row.countInTotal !== false;

/** Totals per currency, ignoring anything switched out of the totals. */
export const totalsByCurrency = (rows = []) =>
  rows.reduce((acc, row) => {
    if (!counts(row)) return acc;
    const code = row.currency || "INR";
    acc[code] = (acc[code] || 0) + (Number(row.value) || 0);
    return acc;
  }, {});

export const totalsByOwner = (rows = [], currency) =>
  rows.reduce((acc, row) => {
    if (!counts(row) || (currency && row.currency !== currency)) return acc;
    const who = row.owner || "joint";
    acc[who] = (acc[who] || 0) + (Number(row.value) || 0);
    return acc;
  }, {});

export const groupByBucket = (rows = []) => {
  const map = new Map(BUCKETS.map((bucket) => [bucket, []]));
  rows.forEach((row) => {
    const bucket = BUCKETS.includes(row.bucket) ? row.bucket : "In hand";
    map.get(bucket).push(row);
  });
  return Array.from(map.entries())
    .map(([bucket, items]) => ({
      bucket,
      items: items.slice().sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0)),
      total: items.reduce((sum, row) => sum + (counts(row) ? Number(row.value) || 0 : 0), 0),
    }))
    .filter((entry) => entry.items.length > 0);
};

/**
 * The household total on each date something was recorded, for the chart.
 * Each account contributes its latest value at or before that date, so the line
 * is what the savings were worth then, not a running sum of edits.
 */
export const netWorthOverTime = (rows = [], currency) => {
  const accounts = rows.filter((row) => counts(row) && (!currency || row.currency === currency));
  const instants = new Set();
  accounts.forEach((row) =>
    (row.history || []).forEach((point) => {
      if (point?.at) instants.add(String(point.at));
    })
  );

  return Array.from(instants)
    .sort()
    .map((instant) => ({
      at: instant,
      value: accounts.reduce((sum, row) => {
        const points = (row.history || [])
          .filter((point) => point?.at && String(point.at) <= instant)
          .sort((a, b) => String(b.at).localeCompare(String(a.at)));
        return sum + (points.length ? Number(points[0].value) || 0 : 0);
      }, 0),
    }));
};

export const symbolFor = (code) => {
  const all = getAllAvailableCurrencies();
  return (all.find((c) => c.code === code) || CURRENCIES[0]).symbol;
};

/** 1234567 in rupees reads 12,34,567; in dollars 1,234,567. */
export const formatAmount = (value, code = "INR") => {
  const amount = Number(value) || 0;
  try {
    return amount.toLocaleString(code === "INR" ? "en-IN" : "en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  } catch {
    return String(Math.round(amount));
  }
};

export default getSavings;
